export interface CidrValidationResult {
  isValid: boolean;
  errorMessage?: string;
  ipAddress?: string;
  prefixLength?: number;
  networkType?: 'IPv4' | 'IPv6';
}

/**
 * Validates a CIDR notation string.
 * Returns a validation result with detailed error messages.
 */
export function validateCidr(cidr: string, mode: 'cidr' | 'ip' = 'cidr'): CidrValidationResult {
  if (!cidr || cidr.trim() === '') {
    return { isValid: false, errorMessage: 'Value is required' };
  }

  const trimmed = cidr.trim();
  const parts = trimmed.split('/');

  if (mode === 'cidr' && parts.length !== 2) {
    return {
      isValid: false,
      errorMessage: 'Invalid format. Expected: ip/prefix (e.g., 192.168.1.0/24)',
    };
  }

  if (mode === 'ip' && parts.length !== 1) {
     return {
      isValid: false,
      errorMessage: 'Invalid format. Expected IP address (e.g., 192.168.1.1)',
    };
  }

  const ipPart = parts[0];
  const prefixPart = parts[1]; // Undefined if mode='ip'

  // Check if it's IPv4 or IPv6
  const isIPv6 = ipPart.includes(':');
  const isIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ipPart);

  if (!isIPv4 && !isIPv6) {
    return {
      isValid: false,
      errorMessage: `Invalid IP address: ${ipPart}`,
    };
  }

  // Validate IPv4 address
  if (isIPv4) {
    const octets = ipPart.split('.');
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (isNaN(num) || num < 0 || num > 255) {
        return {
          isValid: false,
          errorMessage: `Invalid octet "${octet}" in IP address. Must be 0-255.`,
        };
      }
    }
  }

  // Validate IPv6 address (basic check)
  if (isIPv6) {
    // Simple validation: check for valid hex characters and colons
    const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$|^::([0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{0,4}$|^([0-9a-fA-F]{1,4}:){1,6}::?$/;
    if (!ipv6Regex.test(ipPart) && ipPart !== '::') {
      return {
        isValid: false,
        errorMessage: 'Invalid IPv6 address format',
      };
    }
  }

  let prefix: number | undefined;
  
  if (mode === 'cidr') {
      // Validate prefix length
      prefix = parseInt(prefixPart, 10);
      if (isNaN(prefix) || prefixPart.includes('.')) {
        return {
          isValid: false,
          errorMessage: `Invalid prefix length: "${prefixPart}"`,
        };
      }
    
      const maxPrefix = isIPv4 ? 32 : 128;
      if (prefix < 0 || prefix > maxPrefix) {
        return {
          isValid: false,
          errorMessage: `Prefix must be 0-${maxPrefix} for ${isIPv4 ? 'IPv4' : 'IPv6'}`,
        };
      }
  }

  return {
    isValid: true,
    ipAddress: ipPart,
    prefixLength: prefix,
    networkType: isIPv4 ? 'IPv4' : 'IPv6',
  };
}

/**
 * Calculates the number of usable hosts in a CIDR block
 */
export function calculateCidrHosts(prefixLength: number, isIPv6: boolean): string {
  if (prefixLength === 0) {
    return 'All network traffic';
  }

  const maxBits = isIPv6 ? 128 : 32;
  const hostBits = maxBits - prefixLength;

  if (hostBits > 20) {
    // For very large networks, show a simplified message
    return 'Very large network';
  }
  
  const totalAddresses = Math.pow(2, hostBits);
  
  if (totalAddresses === 1) {
    return '1 host';
  }
  
  // For IPv4 /31 and /32, all addresses are usable
  // For larger networks, subtract 2 for network and broadcast
  const usableHosts = prefixLength >= 31 ? totalAddresses : totalAddresses - 2;
  
  if (usableHosts <= 0) {
    return '1 host';
  }
  
  return `${usableHosts.toLocaleString()} hosts`;
}
